import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { MapPin, LogIn, LogOut, Navigation, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Opt-in geofenced site sign-in for field workers. When location sharing
 * is on and the app is open, we check the worker's position against
 * today's dispatched jobs and offer one-tap sign-in on arrival (and a
 * sign-out nudge after leaving). Location is only read while the app is
 * open — nothing is tracked in the background.
 */
export function GeofenceSignin() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(user?.location_sharing === true);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("fl_geo_prompt_hidden") === "1");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  const getPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      reject, { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  });

  const runCheck = async () => {
    try {
      const pos = await getPosition();
      const { data } = await api.post("/geofence/check", pos);
      setCheck({ ...data, pos });
    } catch { /* no permission / no fix — stay quiet */ }
  };

  useEffect(() => {
    if (!enabled) return;
    runCheck();
    const t = setInterval(runCheck, 4 * 60 * 1000);
    return () => clearInterval(t);
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async () => {
    setBusy(true);
    try {
      await getPosition(); // triggers the browser permission prompt
      await api.post("/geofence/opt", { enabled: true });
      setEnabled(true);
      toast.success("Auto sign-in on — we'll spot you when you arrive on site");
    } catch {
      toast.error("Location permission is needed for auto sign-in");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    try { await api.post("/geofence/opt", { enabled: false }); } catch {}
    setEnabled(false);
    setCheck(null);
    toast.success("Location sharing off");
  };

  const signIn = async (job) => {
    setBusy(true);
    try {
      await api.post("/geofence/signin", { job_id: job.job_id, ...check.pos });
      toast.success(`Signed in at ${job.title}`);
      runCheck();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-in failed");
    } finally { setBusy(false); }
  };

  const signOut = async (signinId, title) => {
    setBusy(true);
    try {
      await api.post("/geofence/signout", { signin_id: signinId });
      toast.success(`Signed out of ${title || "site"}`);
      runCheck();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-out failed");
    } finally { setBusy(false); }
  };

  // ---- Opt-in card (dismissible) ----
  if (!enabled) {
    if (dismissed) return null;
    return (
      <div className="rounded-[20px] bg-white dark:bg-[#101620] border border-zinc-200 dark:border-[#1F2937] p-4 mb-4 flex items-start gap-3.5" data-testid="geofence-optin-card">
        <div className="w-11 h-11 rounded-xl bg-[#FF5F15] flex items-center justify-center shrink-0 shadow-lg shadow-[#FF5F15]/25">
          <Navigation className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold uppercase tracking-tight text-[15px] text-zinc-900 dark:text-[#F8FAFC]">Auto site sign-in</div>
          <p className="text-xs text-zinc-600 dark:text-[#94A3B8] mt-1 leading-relaxed">
            Share your location and we'll offer one-tap sign-in when you arrive at a job.
            Only checked while the app is open — never tracked in the background.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <Button size="sm" onClick={enable} disabled={busy}
                    className="h-9 rounded-xl bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold uppercase tracking-wider text-[10px] px-4"
                    data-testid="geofence-enable-btn">
              {busy ? "Enabling…" : "Turn on"}
            </Button>
            <button type="button"
                    onClick={() => { localStorage.setItem("fl_geo_prompt_hidden", "1"); setDismissed(true); }}
                    className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 px-2 py-2"
                    data-testid="geofence-dismiss-btn">
              No thanks
            </button>
          </div>
        </div>
      </div>
    );
  }

  const nearby = (check?.nearby || []).filter((n) => !n.signed_in);
  const onSite = (check?.nearby || []).find((n) => n.signed_in);
  const active = check?.active_signin;

  return (
    <div className="mb-4 space-y-2" data-testid="geofence-panel">
      {/* Arrival prompt */}
      {nearby.map((j) => (
        <div key={j.job_id} className="hard-card p-3 border-l-4 border-l-[#FF5F15] flex items-center gap-3"
             data-testid={`geofence-arrive-${j.job_id}`}>
          <MapPin className="w-5 h-5 text-[#FF5F15] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate">You're at {j.title}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{j.distance_m}m from site pin</div>
          </div>
          <Button size="sm" onClick={() => signIn(j)} disabled={busy}
                  className="h-9 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] font-bold uppercase tracking-wider text-[10px] shrink-0"
                  data-testid={`geofence-signin-btn-${j.job_id}`}>
            <LogIn className="w-3.5 h-3.5 mr-1" /> Sign in
          </Button>
        </div>
      ))}

      {/* Left-site nudge */}
      {active?.left_site && (
        <div className="hard-card p-3 border-l-4 border-l-amber-500 flex items-center gap-3"
             data-testid="geofence-left-site">
          <LogOut className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate">Left {active.job_title}?</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              You're {active.distance_m}m away and still signed in
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => signOut(active.signin_id, active.job_title)} disabled={busy}
                  className="h-9 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[10px] shrink-0"
                  data-testid="geofence-signout-btn">
            Sign out
          </Button>
        </div>
      )}

      {/* Quiet status line */}
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400 px-1"
           data-testid="geofence-status">
        <Navigation className="w-3 h-3" />
        {onSite ? (
          <>Signed in at {onSite.title} · {onSite.distance_m}m
            <button type="button" onClick={() => signOut(onSite.signin_id, onSite.title)}
                    className="underline hover:text-zinc-600" data-testid="geofence-status-signout">sign out</button>
          </>
        ) : (
          <>Auto sign-in on</>
        )}
        <button type="button" onClick={disable} className="ml-auto flex items-center gap-1 hover:text-zinc-600"
                data-testid="geofence-disable-btn">
          <X className="w-3 h-3" /> Turn off
        </button>
      </div>
    </div>
  );
}
